import { ReactElement, useState, useRef, useEffect } from 'react';
import { IProductVariantFields } from 'types/contentful';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { FRAMES_IMAGE_PLACEHOLDER } from 'lib/consts';
import { buildSrcSet } from 'lib/util';

const MAX_IMAGE_WIDTH = 640;

export default function ProductImages({
  primaryFrameImage,
  alternateFrameImages,
  upc,
}: IProductVariantFields): ReactElement {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageWidth, setImageWidth] = useState(MAX_IMAGE_WIDTH);
  const divWrapper = useRef<HTMLInputElement>(null);

  const imageAssets = [];

  if (primaryFrameImage) {
    imageAssets.push(primaryFrameImage);
  }
  imageAssets.push(...(alternateFrameImages || []));

  const images =
    imageAssets.length > 0
      ? imageAssets.map((image, i) => {
          const {
            title,
            description,
            file: { url },
          } = image.fields;
          return (
            <picture key={i}>
              <source srcSet={`${buildSrcSet(url, imageWidth, 2, true)}`} type="image/webp" />
              <img
                src={`${url}?w=${imageWidth}`}
                srcSet={`${buildSrcSet(url, imageWidth, 2)}`}
                style={{ width: imageWidth, height: imageWidth / 2 }}
                alt={description || title}
              />
            </picture>
          );
        })
      : [<img key="placeholder-image" src={FRAMES_IMAGE_PLACEHOLDER} alt="placeholder" />];

  const back = (): void => {
    if (currentIndex - 1 !== -1) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(images.length - 1);
    }
  };

  const forward = (): void => {
    if (currentIndex + 1 !== images.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const Dots = (): ReactElement => {
    const dots = new Array(images.length).fill('');
    const buttonClick = (i: number): void => {
      setCurrentIndex(i);
    };

    return (
      <div>
        {dots.map((dot, i) => (
          <button
            key={i}
            onClick={() => buttonClick(i)}
            className={`rounded-full h-2 w-2 mx-1 transform bg-black ${
              currentIndex === i ? 'scale-125' : 'opacity-25'
            }`}
          ></button>
        ))}
      </div>
    );
  };

  useEffect(() => {
    // give time for the div to be loaded
    // this will get better image sizes if smaller than the max image width
    setTimeout(() => {
      setImageWidth(divWrapper?.current?.offsetWidth || MAX_IMAGE_WIDTH);
    }, 100);
  }, []);

  useEffect(() => {
    // reset Carousel image to first if the variant changes
    setCurrentIndex(0);
  }, [upc]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center">
        <button onClick={back} className="hidden lg:block">
          <ChevronLeftIcon />
        </button>
        <div className="sm:w-pdpImage" ref={divWrapper}>
          <Carousel
            selectedItem={currentIndex}
            autoPlay={false}
            showThumbs={false}
            showStatus={false}
            showArrows={false}
            showIndicators={false}
            onChange={(i) => setCurrentIndex(i)}
            width={''}
            interval={3600000} // 1 hour - to stop the autoscroll bug on mobile in package
          >
            {images}
          </Carousel>
        </div>
        <button onClick={forward} className="hidden lg:block">
          <ChevronRightIcon />
        </button>
      </div>
      <Dots />
    </div>
  );
}
